import React, { ReactElement, useMemo } from "react";
import { showRequestErrorToast } from "../services/ErrorService";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Loading } from "./loading/Loading";
import { useDefects } from "../queries/notableItemQueries";
import { sendGAEvent } from "../services/AnalyticsService";
import { GA_BUTTON_EVENT } from "../constants/GaEvents";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { DefectItems } from "@idwal/idwal-react-components";
import { AppContext } from "../App";
import { AppContextType } from "../types/AppContextType";
import { useAppSelector } from "../redux/hooks";
import {
    defectCodeChipsSelector,
    regimeDescriptionsSelector
} from "../features/inspectionsSlice";

export const Defects = (): ReactElement => {
    const { t } = useTranslation("locale");
    const { inspectionId } = useParams();
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);
    const { earlyRelease } = React.useContext(AppContext) as AppContextType;
    const defectCodes = useAppSelector(defectCodeChipsSelector);
    const descriptions = useAppSelector(regimeDescriptionsSelector);

    if (!inspectionId) {
        console.error("No inspection id.");
        showRequestErrorToast("Defects");
        return <div />;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO(A2-9305): guard clause above returns before this hook runs
    const { data, isError, error, isLoading } = useDefects(inspectionId, user);

    if (isLoading || data == undefined) return <Loading />;

    if (isError) {
        console.error(error);
        showRequestErrorToast("Defects");
        return <div />;
    }

    if (data.length === 0) {
        return (
            <div
                className="flex justify-content-center align-items-center h-3rem"
                style={{ backgroundColor: "#FCFCFC" }}
            >
                {t("defects.noDefectItems")}
            </div>
        );
    }

    const handleAnalyticsEvent = (action: string) => {
        sendGAEvent(GA_BUTTON_EVENT, action);
    };

    if (earlyRelease) {
        return (
            <DefectItems
                notableItems={[]}
                sendAnalyticsEvent={handleAnalyticsEvent}
                showImageGrid={false}
            />
        );
    }

    return (
        <DefectItems
            notableItems={data}
            sendAnalyticsEvent={handleAnalyticsEvent}
            showImageGrid={false}
            defectCodes={defectCodes}
            descriptions={descriptions}
        />
    );
};
